import type { ReactNode } from "react";
import type { AppLocale } from "@akina/infra";
import { cn } from "@akina/utils";

export const TEXT_SIZES = ["5xl", "4xl", "3xl", "2xl", "xl", "lg", "base", "sm", "xs"] as const;

export type TextSize = (typeof TEXT_SIZES)[number];

const MAIN_COLOR_PREFIXES = ["text-white", "text-black", "text-current"] as const;
const COLOR_PREFIXES = [
  "text-orange",
  "text-pink",
  "text-gray",
  "text-primary",
  "text-secondary",
  "text-error",
  "text-warning",
  "text-success",
  "text-info",
] as const;
const COLOR_VERSIONS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;
const COLOR_TRANSPARENCIES = [10, 20, 30, 40, 50, 60, 70, 80, 90] as const;

export const TEXT_COLOR_CLASSES = [
  ...COLOR_PREFIXES.flatMap(prefix =>
    COLOR_VERSIONS.flatMap(version => {
      const variantsWithoutTransparency = `${prefix}-${version}`;
      const variantsWithTransparency = COLOR_TRANSPARENCIES.map(transparency => `${prefix}-${version}/${transparency}`);
      return [variantsWithoutTransparency, ...variantsWithTransparency];
    }),
  ),
  ...MAIN_COLOR_PREFIXES.flatMap(prefix => COLOR_TRANSPARENCIES.map(transparency => `${prefix}/${transparency}`)),
  ...MAIN_COLOR_PREFIXES,
];

type ColorPrefixes = (typeof COLOR_PREFIXES)[number];
type ColorVersions = (typeof COLOR_VERSIONS)[number];
type ColorTransparencies = (typeof COLOR_TRANSPARENCIES)[number];
type MainColorPrefixes = (typeof MAIN_COLOR_PREFIXES)[number];
export type TextColorClasses =
  | `${ColorPrefixes}-${ColorVersions}`
  | `${ColorPrefixes}-${ColorVersions}/${ColorTransparencies}`
  | `${MainColorPrefixes}/${ColorTransparencies}`
  | MainColorPrefixes;

export const FONT_WEIGHTS_CLASSES = [
  "font-black",
  "font-extrabold",
  "font-bold",
  "font-semibold",
  "font-medium",
  "font-normal",
  "font-light",
  "font-extralight",
  "font-thin",
] as const;

export type FontWeightClasses = (typeof FONT_WEIGHTS_CLASSES)[number];
export type TextProps = {
  children: ReactNode;
  size?: TextSize;
  className?: string;
  color?: TextColorClasses;
  fontWeight?: FontWeightClasses;
  title?: string;
  lang?: AppLocale;
}

const getClasses = (size: TextSize): string => {
  switch (size) {
    case "5xl": {
      return "text-5xl font-area-inktrap";
    }
    case "4xl": {
      return "text-4xl font-area-inktrap";
    }
    case "3xl": {
      return "text-3xl font-area-inktrap";
    }
    case "2xl": {
      return "text-2xl font-area-inktrap";
    }
    case "xl": {
      return "text-xl font-area";
    }
    case "lg": {
      return "text-lg font-area";
    }
    case "base": {
      return "text-base font-area";
    }
    case "sm": {
      return "text-sm font-area";
    }
    case "xs": {
      return "text-xs font-area";
    }
    default: {
      return "font-area";
    }
  }
};

const defaultClasses = ``;

export const Text = ({
  children,
  size = "base",
  className,
  color = `text-gray-800`,
  fontWeight,
  title,
  lang,
}: TextProps) => {
  return (
    <p lang={lang} title={title} className={cn(defaultClasses, getClasses(size), color, className, fontWeight)}>
      {children}
    </p>
  );
};

export type ErrorTextProps = {
  error: string | undefined;
} & Omit<TextProps, "children">

const transition =
  "transition-[height,opacity] duration-200 ease-in-out data-[error=true]:h-fit data-[error=false]:h-0 data-[error=true]:opacity-100 data-[error=false]:opacity-0";

export const ErrorMessage = ({ error, className, ...props }: ErrorTextProps) => {
  return (
    <div data-error={!!error} className={cn(transition, className)}>
      <Text {...props} color="text-error-500">
        {error}
      </Text>
    </div>
  );
};
